import "./styles/app.css";
import "./styles/fonts.css";
import "video.js/dist/video-js.css";
import "swiper/css";

import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { useEffect, useState } from "react";
import { CookiesProvider } from "react-cookie";
import type { LinksFunction, LoaderFunctionArgs, MetaFunction } from "react-router";
import { isRouteErrorResponse, useRouteError } from "react-router";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation,
} from "react-router";

import { getRootData } from "~/services/page";

import BaseLink from "./components/BaseLink";
import type { BlogArticle } from "./lib/types";
import { useRouteData } from "./utils/useRouteData";
import { useSeo } from "./utils/useSeo";

export const shouldRevalidate = () => {
  // This data doesn't change between requests, no need to keep refreshing it.
  return false;
};

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const requestUrl = new URL(request?.url);
  const preview = requestUrl?.searchParams?.get("preview") === process.env.SANITY_PREVIEW_SECRET;
  const data = await getRootData(preview);

  return {
    ua: request.headers.get("user-agent") ?? "unknown",
    data,
    ENV: {
      SANITY_DATASET: process.env.SANITY_DATASET,
      ALGOLIA_INDEX: process.env.ALGOLIA_INDEX,
      ALGOLIA_APPLICATION_ID: process.env.ALGOLIA_APPLICATION_ID,
      ALGOLIA_SITEWIDE_INDEX: process.env.ALGOLIA_SITEWIDE_INDEX,
      ALGOLIA_JOBS_INDEX: process.env.ALGOLIA_JOBS_INDEX,
      MUX_ENV_KEY: process.env.MUX_ENV_KEY,
      ALGOLIA_SEARCH_API_KEY: process.env.ALGOLIA_SEARCH_API_KEY,
      // This is "production" for both preview and production deploys:
      MODE: import.meta.env.MODE,
      // This is only "production" in production deploys:
      VITE_CONTEXT: import.meta.env.VITE_CONTEXT,
    },
  };
};

export const meta: MetaFunction = ({ location }) => {
  const themeColor = location.pathname.startsWith("/library")
    ? "#556F73" // var(--color-darker-steel-gray)
    : "#061D22"; // var(--color-heavy-slate)
  return [
    { "theme-color": themeColor },
    { "apple-mobile-web-app-status-bar-style": themeColor },
    { charset: "utf-8" },
    { name: "viewport", content: "width=device-width,initial-scale=1" },
  ];
};

export const links: LinksFunction = () => {
  return [
    // {
    //   rel: "stylesheet",
    //   href: "https://kit.fontawesome.com/a1f19b781f.css",
    // },
  ];
};

export default function App() {
  const location = useLocation();
  const { ENV, data } = useLoaderData<typeof loader>();

  const seoHead = useSeo(data);

  const getFaviconPath = (isDarkMode = false) => {
    return `/icons/favicon-${isDarkMode ? "light" : "dark"}.ico`;
  };

  const [faviconHref, setFaviconHref] = useState("/icons/favicon-dark.ico");
  const routeData = useRouteData();
  const templateType: BlogArticle["templateType"] = routeData?.templateType;

  useEffect(() => {
    const matcher = window.matchMedia("(prefers-color-scheme: dark)");
    setFaviconHref(getFaviconPath(matcher.matches));
    matcher.onchange = () => setFaviconHref(getFaviconPath(matcher.matches));
  }, [faviconHref]);

  useEffect(() => {
    addGtmScript();
    addHsScript();
  }, []);

  return (
    <html lang="en">
      <head>
        <script
          dangerouslySetInnerHTML={{
            __html: `
window.VIDEOJS_NO_DYNAMIC_STYLE = true;
window.ENV = ${JSON.stringify(ENV)};
`,
          }}
        />

        <Meta />
        <Links />
        {seoHead}
        <link rel="icon" href={faviconHref} />
      </head>
      <body
        className={`min-h-screen lining-nums ${templateType} ${templateType === "10yoc"
          ? "bg-heavy-slate antialiased"
          : location.pathname.startsWith("/library")
            ? "bg-darker-steel-gray subpixel-antialiased"
            : "bg-heavy-slate antialiased"
          }`}
      >
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-MH5753"
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
          ></iframe>
        </noscript>

        <PostHogProvider
          apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
          options={{
            api_host: "/relay-ph",
            ui_host: "https://us.posthog.com",
            defaults: "2025-05-24",
            capture_exceptions: true,
            debug: import.meta.env.POSTHOG_DEBUG,
          }}
        >
          <CookiesProvider>
            <Outlet context={data} />
            <ScrollRestoration />
          </CookiesProvider>
        </PostHogProvider>

        <Scripts />
      </body>
    </html>
  );
}

let gtmScriptAdded = false;
let hsScriptAdded = false;

declare global {
  interface Window {
    [key: string]: object[];
  }
}

function addHsScript(HS_ID: string = "44308902") {
  if (!HS_ID || hsScriptAdded) {
    return;
  }

  // Code copied from GTM console + added type annotations.
  (function(d: Document, s: "script", i: string) {
    const f = d.getElementsByTagName(s)[0];
    const j = d.createElement<"script">(s);
    j.async = true;
    j.defer = true;
    j.id = "hs-script-loader";
    j.src = `//js.hs-scripts.com/${i}.js`;
    f.parentNode?.insertBefore(j, f);
  })(document, "script", HS_ID);

  hsScriptAdded = true;
}

function addGtmScript(GTM_ID: string = "GTM-MH5753") {
  if (!GTM_ID || gtmScriptAdded) {
    return;
  }

  // Code copied from GTM console + added type annotations.
  (function(w: Window, d: Document, s: "script", l: string, i: string) {
    w[l] = w[l] || [];
    w[l].push({
      "gtm.start": new Date().getTime(),
      event: "gtm.js",
    });
    const f = d.getElementsByTagName(s)[0];
    const j = d.createElement<"script">(s);
    const dl = l != "dataLayer" ? "&l=" + l : "";
    j.async = true;
    j.defer = true;
    j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
    f.parentNode?.insertBefore(j, f);
  })(window, document, "script", "dataLayer", GTM_ID);

  gtmScriptAdded = true;
}

export function ErrorBoundary() {
  const error = useRouteError();
  posthog.captureException(error)

  return (
    <html>
      <head>
        <title>Something went wrong</title>
        <Meta />
        <Links />
      </head>
      <body className="bg-heavy-slate">
        <section className="error-page-bg flex min-h-svh items-center justify-center pt-[100px] pb-[150px]">
          <div className="font-planar relative flex max-w-[600px] flex-col items-center text-center text-white">
            {isRouteErrorResponse(error) ? (
              <>
                <h1 className="text-title2 mb-4">
                  {error.status}: {error.statusText}
                </h1>
                {import.meta.env.MODE !== "production" && (
                  <p className="text-light-steel-gray font-plexsans text-sm">{error.data}</p>
                )}
              </>
            ) : error instanceof Error ? (
              <>
                <h1 className="text-title2">Error</h1>
                <p>{error.message}</p>
              </>
            ) : (
              <h1 className="text-title2">Unknown Error</h1>
            )}

            <BaseLink
              className="bg-orange font-plexsans mt-8 rounded-[20px] px-[20px] py-[10px] text-white"
              href="/"
            >
              Back to Homepage
            </BaseLink>
          </div>
        </section>
        <Scripts />
      </body>
    </html>
  );
}
